/** Symbol used to separate station label parts. "+" is rail, "/" is road. */
export type LabelSymbol = '+' | '/';

/**
 * Label format template controlling how chainage values are displayed.
 *
 * The template characters have the following meanings:
 * - `1` and `0` represent digit positions in the integer part.
 * - The **last** `.` in the template marks the decimal point.
 * - Any earlier `.` becomes a literal thousands-separator dot in the output.
 * - A `+` or `/` character is a symbol separator inserted at that position.
 *
 * Examples (with symbol `+`):
 * - `"1000.0"` → plain, at least 4 integer digits, e.g. `"0150.00"`
 * - `"10+00.0"` → symbol before last 2 integer digits, e.g. `"01+50.00"`
 * - `"1+000.0"` → symbol before last 3 integer digits, e.g. `"0+150.00"`
 * - `"1.0+00.0"` → thousands dot + symbol, e.g. `"0.1+50.00"`
 */
export type LabelFormat = string;

/** Coordinate axis order for alignment display. */
export type AlignmentCoordinateOrder = 'XYZ' | 'YXZ' | 'ENH' | 'NEH';

export const ALIGNMENT_COORDINATE_ORDER_OPTIONS: Record<
  AlignmentCoordinateOrder,
  string
> = {
  XYZ: 'X, Y, Z',
  YXZ: 'Y, X, Z',
  ENH: 'E, N, H',
  NEH: 'N, E, H',
};

export interface AlignmentSettings {
  /** Station label separator symbol. */
  labelSymbol: LabelSymbol;

  /** Format template for station labels (symbol chars must match labelSymbol). */
  labelFormat: LabelFormat;

  /** Vertical (Z) offset of station labels from the alignment, in meters. */
  verticalOffset: number;

  /** Horizontal offset of station labels perpendicular to the alignment, in meters. */
  horizontalOffset: number;

  /** Size multiplier for station labels. */
  labelSize: number;

  /** Spacing between station labels in meters (passed to Alignment API as stationLabelSpacing). */
  labelInterval: number;

  /** Display coordinate order. */
  coordinateOrder: AlignmentCoordinateOrder;
}

export const DEFAULT_ALIGNMENT_SETTINGS: AlignmentSettings = {
  labelSymbol: '+',
  labelFormat: '1000.0',
  verticalOffset: 0.25,
  horizontalOffset: 0,
  labelSize: 1,
  labelInterval: 10,
  coordinateOrder: 'XYZ',
};

/**
 * Returns the available label format options for a given symbol.
 * The format templates are the same structure, only the symbol character differs.
 */
export function getLabelFormatOptions(symbol: LabelSymbol): LabelFormat[] {
  return ['1000.0', `10${symbol}00.0`, `1${symbol}000.0`, `1.0${symbol}00.0`];
}

/**
 * When the label symbol changes, the current format must be updated to use the
 * new symbol character.  If the current format doesn't contain the old symbol
 * (e.g. `"1000.0"`), it is returned unchanged.
 */
export function migrateLabelFormat(
  currentFormat: LabelFormat,
  newSymbol: LabelSymbol
): LabelFormat {
  const oldSymbol: LabelSymbol = newSymbol === '+' ? '/' : '+';
  return currentFormat.replaceAll(oldSymbol, newSymbol);
}
